import { useEffect, useState, useContext } from "react";

import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";

import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

import uniqueData from "helpers/UniqueData";
import sortString from "helpers/SortString";
import { updateQuery } from "api/UdasWrapper";
import { useAuth0 } from "@auth0/auth0-react";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { UserContext } from "context/AppProvider/UserContext";
import { PERMISSIONS } from "constants/portal";
import { getEnv } from "helpers/environment";

export const permissionsArrangement = {
  [PERMISSIONS.ADD_CREDIT_CARD]: 2,
  [PERMISSIONS.ACCESS_TO_BILLING_PAGE]: 1,
  [PERMISSIONS.UPDATE_BILLING_INFO]: 4,
  [PERMISSIONS.REMOVE_CREDIT_CARD]: 3,
  [PERMISSIONS.ACCESS_TO_COMPANY_EDIT_PAGE]: 2,
  [PERMISSIONS.DELETE_COMPANY_ACCOUNT]: 3,
  [PERMISSIONS.ACCESS_TO_COMPANY_PAGE]: 1,
  [PERMISSIONS.DEACTIVATE_COMPANY_ACCOUNT]: 4,
  [PERMISSIONS.ACCESS_TO_WORKFLOWS_PAGE]: 2,
  [PERMISSIONS.EDIT_WORKFLOWS]: 6,
  [PERMISSIONS.ACCESS_TO_DESIGNER]: 1,
  [PERMISSIONS.SHARE_WORKFLOWS]: 7,
  [PERMISSIONS.RETRIEVE_WORKFLOWS]: 9,
  [PERMISSIONS.CREATE_WORKFLOWS]: 4,
  [PERMISSIONS.DELETE_WORKFLOWS]: 8,
  [PERMISSIONS.ACTIVATE_WORKFLOWS]: 3,
  [PERMISSIONS.DELETE_OWN_ACCOUNT]: 2,
  [PERMISSIONS.DEACTIVATE_WORKFLOWS]: 5,
  [PERMISSIONS.DEACTIVATE_OWN_ACCOUNT]: 3,
  [PERMISSIONS.ACCESS_TO_ACCOUNT_SETTINGS]: 1,
  [PERMISSIONS.UNREGISTER_COMPONENT]: 4,
  [PERMISSIONS.UNPUBLISH_FLOW]: 2,
  [PERMISSIONS.PUBLISH_FLOW]: 1,
  [PERMISSIONS.PUBLISH_AND_UNPUBLISH_COMPONENTS]: 5,
  [PERMISSIONS.REGISTER_COMPONENTS]: 3,
  [PERMISSIONS.PURCHASE_COMPONENTS]: 6,
  [PERMISSIONS.REMOVE_ROLES]: 4,
  [PERMISSIONS.EDIT_ROLES]: 3,
  [PERMISSIONS.ACCESS_TO_ROLES_AND_PERMISSIONS_PAGE]: 1,
  [PERMISSIONS.CREATE_ROLES]: 2,
  [PERMISSIONS.ACCESS_TO_SUBSCRIPTIONS_PAGE]: 1,
  [PERMISSIONS.MANAGE_SUBSCRIPTION]: 2,
  [PERMISSIONS.CREATE_SUPPORT_TICKET]: 1,
  [PERMISSIONS.VIEW_TICKETS]: 2,
  [PERMISSIONS.INVITE_USERS]: 2,
  [PERMISSIONS.EDIT_USERS]: 4,
  [PERMISSIONS.SUSPEND_USERS]: 7,
  [PERMISSIONS.UNSUSPEND_USERS]: 8,
  [PERMISSIONS.TRANSFER_OWNERSHIP]: 4,
  [PERMISSIONS.ACCESS_TO_USERS_PAGE]: 1,
  [PERMISSIONS.RESTORE_USERS]: 6,
  [PERMISSIONS.ASSIGN_ROLE]: 3,
  [PERMISSIONS.REMOVE_USERS]: 5,
  [PERMISSIONS.ACCESS_TO_CONNECTOR_APPROVAL_PAGE]: 1,
  [PERMISSIONS.ACCESS_TO_CUSTOMER_SUBSCRIPTIONS_PAGE]: 2,
  [PERMISSIONS.APPROVE_PUBLISHED_CONNECTOR]: 3,
  [PERMISSIONS.ACCESS_TO_ACCOUNT_PAGE]: 1,
  [PERMISSIONS.ACCESS_TO_QUICK_START_PAGE]: 1,
  [PERMISSIONS.CREATE_CREDENTIALS]: 2,
  [PERMISSIONS.DELETE_CREDENTIALS]: 3,
  [PERMISSIONS.MODIFY_CREDENTIALS]: 4,
  [PERMISSIONS.SHARE_CREDENTIALS]: 5,
  [PERMISSIONS.VIEW_CREDENTIALS]: 6,
  [PERMISSIONS.ACCESS_TO_CREDENTIAL_MANAGEMENT_PAGE]: 1,
  [PERMISSIONS.ACCESS_TO_CONNECTORS_PAGE]: 1,
  [PERMISSIONS.CREATE_CONNECTORS]: 2,
  [PERMISSIONS.DELETE_CONNECTORS]: 3,
  [PERMISSIONS.EDIT_CONNECTORS]: 4,
  [PERMISSIONS.SHARE_CONNECTORS]: 5,
  [PERMISSIONS.ACCESS_TO_LOGS_PAGE]: 1,
  [PERMISSIONS.ACCESS_TO_SCHEDULER_PAGE]: 1,
  [PERMISSIONS.ADD_SCHEDULES]: 2,
  [PERMISSIONS.DELETE_SCHEDULES]: 3,
  [PERMISSIONS.EDIT_SCHEDULES]: 4,
  [PERMISSIONS.PAUSE_SCHEDULES]: 5,
  [PERMISSIONS.VIEW_ANY_WORKFLOWS]: 12,
  [PERMISSIONS.EDIT_ANY_WORKFLOWS]: 13,
  [PERMISSIONS.DELETE_ANY_WORKFLOWS]: 14,
  [PERMISSIONS.SHARE_ANY_WORKFLOWS]: 15,
  [PERMISSIONS.VIEW_ANY_INTEGRATIONS]: 1,
  [PERMISSIONS.EDIT_ANY_INTEGRATIONS]: 2,
  [PERMISSIONS.DELETE_ANY_INTEGRATIONS]: 3,
  [PERMISSIONS.SHARE_ANY_INTEGRATIONS]: 4,
  [PERMISSIONS.VIEW_ANY_CONNECTORS]: 6,
  [PERMISSIONS.EDIT_ANY_CONNECTORS]: 7,
  [PERMISSIONS.DELETE_ANY_CONNECTORS]: 8,
  [PERMISSIONS.SHARE_ANY_CONNECTORS]: 9,
  [PERMISSIONS.VIEW_ANY_CREDENTIALS]: 7,
  [PERMISSIONS.EDIT_ANY_CREDENTIALS]: 8,
  [PERMISSIONS.DELETE_ANY_CREDENTIALS]: 9,
  [PERMISSIONS.SHARE_ANY_CREDENTIALS]: 10,
  [PERMISSIONS.VIEW_ANY_SCHEDULES]: 6,
  [PERMISSIONS.EDIT_ANY_SCHEDULES]: 7,
  [PERMISSIONS.DELETE_ANY_SCHEDULES]: 8,
  [PERMISSIONS.SHARE_ANY_SCHEDULES]: 9
};

const parentPermissions = {
  Billing: [PERMISSIONS.ACCESS_TO_BILLING_PAGE],
  Workflows: [PERMISSIONS.ACCESS_TO_DESIGNER, PERMISSIONS.ACCESS_TO_WORKFLOWS_PAGE],
  "Roles and Permissions": [PERMISSIONS.ACCESS_TO_ROLES_AND_PERMISSIONS_PAGE],
  Subscriptions: [PERMISSIONS.ACCESS_TO_SUBSCRIPTIONS_PAGE],
  Company: [PERMISSIONS.ACCESS_TO_COMPANY_PAGE],
  Users: [PERMISSIONS.ACCESS_TO_USERS_PAGE],
  "Admin access": [PERMISSIONS.ACCESS_TO_CONNECTOR_APPROVAL_PAGE],
  Home: [PERMISSIONS.ACCESS_TO_ACCOUNT_SETTINGS],
  Support: [],
  Marketplace: [],
  Documentation: [],
  "Quick Start": [PERMISSIONS.ACCESS_TO_QUICK_START_PAGE],
  Account: [PERMISSIONS.ACCESS_TO_ACCOUNT_PAGE],
  Credentials: [PERMISSIONS.ACCESS_TO_CREDENTIAL_MANAGEMENT_PAGE],
  Connectors: [PERMISSIONS.ACCESS_TO_CONNECTORS_PAGE],
  Logs: [PERMISSIONS.ACCESS_TO_LOGS_PAGE],
  Schedule: [PERMISSIONS.ACCESS_TO_SCHEDULER_PAGE],
  "Super user": [],
  Integrations: []
};

function Permissions({ selectedRole, roleId, userRoles }) {
  const { user } = useAuth0();
  const { permissions, setPermissions } = useContext(PermissionsContext);
  const [categories, setCategories] = useState(null);
  const { userData } = useContext(UserContext);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [filteredPermissions, setFilteredPermissions] = useState([]);

  const updateRoleId = async (stripeId, permissionId, role_id) => {
    let data = JSON.stringify({
      query: `mutation UpdatePermissions($tenant_id: String!, $updatePermissionsId: String!, $input: PermissionsInput) {
      updatePermissions(tenant_id: $tenant_id, id: $updatePermissionsId, input: $input) {
        isSuccessful
        message
        resultObject
      }
    }`,
      variables: {
        tenant_id: `${stripeId}`,
        updatePermissionsId: `${permissionId}`,
        input: { role_id: role_id }
      }
    });
    try {
      return (await updateQuery(data))?.updatePermissions;
    } catch (error) {
      console.log(error);
    }
  };

  const checkActiveParentPermissions = (category, roleId) => {
    return permissions.filter(
      (permission) =>
        parentPermissions[category].includes(permission.name) &&
        permission.role_id?.includes(roleId)
    );
  };

  const checkActiveChildPermissions = (category, roleId) => {
    return permissions.filter(
      (permission) =>
        !parentPermissions[category].includes(permission.name) &&
        permission.role_id?.includes(roleId) &&
        permission.category === category
    );
  };

  const getInactiveParentPermissions = (category, roleId) => {
    return permissions.filter(
      (permission) =>
        parentPermissions[category].includes(permission.name) &&
        !permission.role_id?.includes(roleId)
    );
  };

  const handlePermissionChange = async (permission, roleId, isChild) => {
    try {
      let activeParentPermissions;
      let updatedPermissions = permissions;
      if (!permission.role_id?.includes(roleId)) {
        // check if it is a child permission
        if (isChild) {
          // check if a parent permission is active
          activeParentPermissions = checkActiveParentPermissions(permission.category, roleId);
          if (activeParentPermissions.length === 0) {
            // check parent permissions that is inactive
            const inactiveParentPermissions = getInactiveParentPermissions(
              permission.category,
              roleId
            );
            // update the role id of the inactive parent permissions
            await Promise.all(
              inactiveParentPermissions.map(async (permission) => {
                await updateRoleId(user?.stripeId, permission.id, [...permission.role_id, roleId]);
                setPermissions((prev) =>
                  prev.map((item) => {
                    if (item.id === permission.id) {
                      return {
                        ...item,
                        role_id: [...item.role_id, roleId]
                      };
                    } else {
                      return item;
                    }
                  })
                );
                updatedPermissions = updatedPermissions.map((item) => {
                  if (item.id === permission.id) {
                    return {
                      ...item,
                      role_id: [...item.role_id, roleId]
                    };
                  } else {
                    return item;
                  }
                });
              })
            );
          }
        }
        // update the role id of the permission
        await updateRoleId(user?.stripeId, permission.id, [...permission.role_id, roleId]);
        setPermissions((prev) =>
          prev.map((item) => {
            if (item.id === permission.id) {
              return {
                ...item,
                role_id: [...item.role_id, roleId]
              };
            } else {
              return item;
            }
          })
        );
        updatedPermissions = updatedPermissions.map((item) => {
          if (item.id === permission.id) {
            return {
              ...item,
              role_id: [...item.role_id, roleId]
            };
          } else {
            return item;
          }
        });
      } else {
        // update the role id of the permission
        setPermissions((prev) =>
          prev.map((item) => {
            if (item.id === permission.id) {
              return {
                ...item,
                role_id: item.role_id?.filter((id) => id !== roleId)
              };
            } else {
              return item;
            }
          })
        );
        updatedPermissions = updatedPermissions.map((item) => {
          if (item.id === permission.id) {
            return {
              ...item,
              role_id: item.role_id?.filter((id) => id !== roleId)
            };
          } else {
            return item;
          }
        });
        await updateRoleId(
          user?.stripeId,
          permission.id,
          permission.role_id?.filter((item) => item !== roleId)
        );

        // check if a parent permission is active
        const activeParentPermissions = updatedPermissions.filter(
          (item) =>
            parentPermissions[permission.category].includes(item.name) &&
            item.role_id?.includes(roleId)
        );
        // check if a child permission is active
        const activeChildPermissions = checkActiveChildPermissions(permission.category, roleId);

        // if a parent permission is inactive and a child permission is active we will update the role id of the child permission to inactive
        if (
          parentPermissions[permission.category]?.length &&
          activeParentPermissions.length === 0 &&
          activeChildPermissions.length > 0
        ) {
          await Promise.all(
            activeChildPermissions.map(async (permission) => {
              await updateRoleId(
                user?.stripeId,
                permission.id,
                permission.role_id?.filter((item) => item !== roleId)
              );
              setPermissions((prev) =>
                prev.map((item) => {
                  if (item.id === permission.id) {
                    return {
                      ...item,
                      role_id: item.role_id?.filter((id) => id !== roleId)
                    };
                  } else {
                    return item;
                  }
                })
              );
              updatedPermissions = updatedPermissions.map((item) => {
                if (item.id === permission.id) {
                  return {
                    ...item,
                    role_id: item.role_id?.filter((id) => id !== roleId)
                  };
                } else {
                  return item;
                }
              });
            })
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const superSetAllowPermission = (permission, currentUserRoleNameLowerCase) => {
    const currentUserRoleId = currentUserRole?.id;

    if (currentUserRoleNameLowerCase === "owner") {
      return true;
    } else if (currentUserRoleNameLowerCase === "admin") {
      return permission.role_id?.includes(currentUserRoleId);
    } else {
      return false;
    }
  };

  const isSubsetPermission = (permission) => {
    const selectedRoleNameLowerCase = selectedRole?.name?.toLowerCase();
    const currentUserRoleNameLowerCase = currentUserRole?.name?.toLowerCase();

    if (selectedRole?.created_by_role_id === currentUserRole?.id) {
      return (
        true &&
        permissions.some(
          (p) => p.name === permission.name && p.role_id.includes(selectedRole?.created_by_role_id)
        )
      );
    }

    switch (selectedRoleNameLowerCase) {
      case "owner":
      case "admin":
        return currentUserRoleNameLowerCase === "owner";
      case "user":
        return (
          (currentUserRoleNameLowerCase === "owner" || currentUserRoleNameLowerCase === "admin") &&
          superSetAllowPermission(permission, currentUserRoleNameLowerCase)
        );
      default:
        return currentUserRoleNameLowerCase === "owner" ? true : false;
    }
  };

  const isTogglePermissionDisabled = (permission) => {
    let isDisabled = !isSubsetPermission(permission);

    return isDisabled;
  };

  const isChild = (permission) => {
    const childSuperUserPermissions = ["Edit all", "Delete all", "Share all"];

    return (
      !parentPermissions[permission.category]?.includes(permission.name) ||
      childSuperUserPermissions.some((childSuperUserPermission) =>
        permission.name.includes(childSuperUserPermission)
      )
    );
  };

  useEffect(() => {
    const query = searchName.toLowerCase();
    const filtered = permissions?.filter((permission) => {
      return (
        permission?.category.toLowerCase().includes(query) ||
        permission?.name.toLowerCase().includes(query) ||
        permission?.description.toLowerCase().includes(query)
      );
    });
    setFilteredPermissions(filtered);
  }, [searchName, permissions]);

  useEffect(() => {
    if (permissions) {
      const unique = uniqueData(permissions, "category");
      setCategories(sortString(unique));
    }
  }, [permissions]);

  useEffect(() => {
    if (userData && userRoles.length) {
      const userRole = userRoles.find((role) => role.id === userData.role_id);
      setCurrentUserRole(userRole);
    }
  }, [userData, userRoles]);

  const renderSearchBar = () => {
    return (
      <SoftBox
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "flex-start",
          padding: "12px 0 12px 40px",
          marginBottom: "1rem"
        }}
      >
        <SoftInput
          size="medium"
          placeholder="Search"
          value={searchName}
          onChange={({ currentTarget }) => setSearchName(currentTarget.value)}
          sx={{ maxWidth: "380px" }}
        />
      </SoftBox>
    );
  };

  const renderPermissionsList = () => {
    if (!categories || categories.length === 0) {
      return (
        <SoftTypography variant="caption" color="text">
          No categories available.
        </SoftTypography>
      );
    }

    return categories.map((category) => {
      const permissionsInCategory = filteredPermissions?.filter(
        (permission) => permission.category === category
      );

      if (!permissionsInCategory || permissionsInCategory.length === 0) {
        return null; // Skip empty categories
      }

      if (getEnv("HIDE_MARKETPLACE_PERMISSIONS") && category === "Marketplace") {
        return null;
      }

      return (
        <SoftBox key={category} mb={3} sx={{ minHeight: 0 }}>
          <Divider className="company-divider" sx={{ mb: 2 }} />
          <SoftTypography
            variant="h6"
            fontWeight="medium"
            color="text"
            sx={{ mb: 2, marginLeft: "2.5rem" }}
          >
            {category}
          </SoftTypography>
          {permissionsInCategory.map((permission, index) => (
            <SoftBox key={permission.id} mb={4} height="clamp(60px, auto, 80px)">
              <Grid container alignItems="center" spacing={2}>
                <Grid size={{ xs: 9 }}>
                  <SoftBox>
                    <SoftTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ display: "block", marginLeft: "2.5rem" }}
                    >
                      {permission.name}
                    </SoftTypography>
                    <SoftTypography
                      variant="caption"
                      fontWeight="regular"
                      color="text"
                      sx={{ display: "block", mt: 0.5, marginLeft: "2.5rem" }}
                    >
                      {permission.description}
                    </SoftTypography>
                  </SoftBox>
                </Grid>

                <Grid size={{ xs: 3 }} textAlign="end">
                  <Switch
                    disabled={isTogglePermissionDisabled(permission)}
                    checked={(permission?.role_id || []).includes(roleId)}
                    onChange={() => handlePermissionChange(permission, roleId, isChild(permission))}
                    sx={{ cursor: "pointer", marginRight: "2.5rem" }}
                  />
                </Grid>
              </Grid>
              {index < permissionsInCategory.length - 1}
            </SoftBox>
          ))}
        </SoftBox>
      );
    });
  };

  return (
    <SoftBox sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      {renderSearchBar()}
      <SoftBox mb={3}>{renderPermissionsList()}</SoftBox>
    </SoftBox>
  );
}

Permissions.propTypes = {
  selectedRole: PropTypes.object.isRequired,
  roleId: PropTypes.string.isRequired,
  userRoles: PropTypes.array.isRequired
};

export default Permissions;
